import axios from 'axios';
import api from './api';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/** Public share links — no session; must not trigger /login redirect on 401. */
const publicHttp = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

publicHttp.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const data = error.response?.data;
    const payload = data?.error || {
      code: 'NETWORK_ERROR',
      message: data?.message || error.message || 'Network error',
    };
    return Promise.reject(payload);
  },
);

const BASE = '/campaign-tracker';

// The shared axios instance already unwraps to the `{ success, data }` body —
// callers read `res.data`, not `res.data.data`.
export const campaignTrackerService = {
  listLocations: () => api.get(`${BASE}/locations`),

  fetchLocationMetrics: (body) => api.post(`${BASE}/locations/metrics`, body),

  listCampaigns: (locationId, params) =>
    api.get(`${BASE}/locations/${encodeURIComponent(locationId)}/campaigns`, { params }),

  /** { service, campaigns: [{ id, name }] } for a Leads Tracker service key */
  getCampaignsByService: (locationId, params) =>
    api.get(`${BASE}/locations/${encodeURIComponent(locationId)}/campaigns-by-service`, { params }),

  // campaignKey is "platform:campaignId" — encoded so the colon survives the path.
  getCampaign: (locationId, campaignKey, params) =>
    api.get(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}`,
      { params },
    ),

  getTrackingScript: (locationId, params) =>
    api.get(`${BASE}/locations/${encodeURIComponent(locationId)}/tracking-script`, { params }),

  listLandingPages: (locationId, campaignKey, params) =>
    api.get(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/landing-pages`,
      { params },
    ),

  addLandingPage: (locationId, campaignKey, body) =>
    api.post(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/landing-pages`,
      body,
    ),

  removeLandingPage: (locationId, campaignKey, id) =>
    api.delete(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/landing-pages/${id}`,
    ),

  getLandingPage: (locationId, campaignKey, pageId, params) =>
    api.get(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/landing-pages/${pageId}`,
      { params },
    ),

  generateLandingPageCallScript: (locationId, campaignKey, pageId) =>
    api.post(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/landing-pages/${pageId}/generate-call-script`,
    ),

  upsertAdPreview: (locationId, campaignKey, body) =>
    api.put(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/ad-preview`,
      body,
    ),

  listAdCreatives: (locationId, campaignKey, params) =>
    api.get(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/ad-creatives`,
      { params },
    ),

  /**
   * RETIRED — both of these now answer 410.
   *
   * Ad copy is created in Ad Approvals and reaches a campaign through an approved,
   * linked session. Kept only so `LiveAdCreativeBlock` (itself no longer mounted)
   * still resolves its imports; delete both when that component goes.
   */
  saveAdCreatives: (locationId, campaignKey, body) =>
    api.put(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/ad-creatives`,
      body,
    ),

  uploadAdCreative: (locationId, campaignKey, file, platform) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('platform', platform);
    return api.post(
      `${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/ad-creatives/upload`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  getClientReportLink: (locationId) =>
    api.get(`${BASE}/locations/${encodeURIComponent(locationId)}/client-report-link`),

  getClientReportEmailDefaults: (locationId, params) =>
    api.get(
      `${BASE}/locations/${encodeURIComponent(locationId)}/client-report-email/compose-defaults`,
      { params },
    ),

  sendClientReportEmail: (locationId, body) =>
    api.post(
      `${BASE}/locations/${encodeURIComponent(locationId)}/client-report-email/send`,
      body,
    ),

  listLiveReportEmails: (params) =>
    api.get(`${BASE}/live-report-emails`, { params }),

  getLiveReportEmail: (id) =>
    api.get(`${BASE}/live-report-emails/${encodeURIComponent(id)}`),

  publicLiveReportBootstrap: (token) =>
    publicHttp.get(`/campaign-tracker-public/live-report/${encodeURIComponent(token)}`),

  publicLiveReportService: (token, serviceKey) =>
    publicHttp.get(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/services/${encodeURIComponent(serviceKey)}`,
    ),

  publicLiveReportContact: (token, body) =>
    publicHttp.post(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/contact`,
      body,
    ),

  publicLiveReportContactQueries: (token, params) =>
    publicHttp.get(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/contact-queries`,
      { params },
    ),

  /** The ad copy a client signed off, resolved through the campaign's approval link. */
  getApprovedContent: (locationId, campaignKey) =>
    api.get(`${BASE}/locations/${encodeURIComponent(locationId)}/campaigns/${encodeURIComponent(campaignKey)}/approved-content`),

  /**
   * The creative approvals waiting on this client, and their decision.
   *
   * Both endpoints have existed — and been rate-limited — since the approval
   * chain was built, and no frontend anywhere called them. So the client was
   * never actually asked. This is the missing half.
   */
  publicLiveReportApprovals: (token) =>
    publicHttp.get(`/campaign-tracker-public/live-report/${encodeURIComponent(token)}/approvals`),
  publicDecideApproval: (token, id, body) =>
    publicHttp.post(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/approvals/${encodeURIComponent(id)}/decide`,
      body,
    ),

  /**
   * The detail behind an ad approval — the campaign plan, the keyword plan and
   * every ad copy, so the client can comment on one ad rather than the whole set.
   * Only ever returns the version currently pending their decision.
   */
  publicAdApprovalSession: (token, sessionId, params) =>
    publicHttp.get(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/ad-approvals/${encodeURIComponent(sessionId)}`,
      { params },
    ),
  publicAddAdApprovalComment: (token, sessionId, body) =>
    publicHttp.post(
      `/campaign-tracker-public/live-report/${encodeURIComponent(token)}/ad-approvals/${encodeURIComponent(sessionId)}/comments`,
      body,
    ),

  listLiveReportQueries: (params) =>
    api.get(`${BASE}/live-report-queries`, { params }),

  getLiveReportQuery: (id) =>
    api.get(`${BASE}/live-report-queries/${encodeURIComponent(id)}`),

  updateLiveReportQueryStatus: (id, body) =>
    api.patch(`${BASE}/live-report-queries/${encodeURIComponent(id)}`, body),
};

export default campaignTrackerService;
